import React, { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, ArrowRight, UserPlus, CheckCircle, Shield, Mic, MicOff, Volume2, VolumeX, MessageCircle, ChevronDown, ChevronUp, Calendar, Clock, Mail, Phone as PhoneIcon, MessageSquare, X, Users, Eye, EyeOff } from 'lucide-react'
import { Button } from '../ui/Button'
import { llmApi } from '../../services/api/llm'
import { Input } from '../ui/Input'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { cn } from '@/lib/utils'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import toast from 'react-hot-toast'
import { elevenLabsService } from '../../services/elevenlabs'

interface MessageButton {
  action: string
  label: string
  icon?: React.ReactNode
}

interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  kind?: 'registrationForm'
  buttons?: MessageButton[]
  bookCallData?: {
    exchangeType?: 'reverse' | 'tic' | 'complex'
    timeSlots?: string[]
    showDateInput?: boolean
    showNotesInput?: boolean
  }
}

interface ChatInterfaceProps {
  onStartProcess: () => void
  className?: string
  bookCallIntent?: boolean
  onBookCallIntentHandled?: () => void
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  onStartProcess, 
  className,
  bookCallIntent = false,
  onBookCallIntentHandled
}) => {
  const { isAuthenticated, user, register: registerUser } = useAuth()
  const navigate = useNavigate()
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)
  const [registrationFormMessageId, setRegistrationFormMessageId] = useState<string | null>(null)
  const [showSSOModal, setShowSSOModal] = useState(false)
  const [ssoProvider, setSSOProvider] = useState<'apple' | 'google' | null>(null)
  useBodyScrollLock(showSSOModal)
  const [registrationPasswordVisible, setRegistrationPasswordVisible] = useState(false)
  const [registrationConfirmPasswordVisible, setRegistrationConfirmPasswordVisible] = useState(false)
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  })

  // Voice controls (STT only - TTS is ElevenLabs only)
  const [isRecording, setIsRecording] = useState(false)
  const [autoSpeak, setAutoSpeak] = useState(true) // Enable by default so Neela speaks
  const [hasVoiceCapabilities, setHasVoiceCapabilities] = useState({ stt: false })
  const recognitionRef = useRef<any>(null)

  useEffect(() => {
    const SpeechRec: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const stt = Boolean(SpeechRec)
    setHasVoiceCapabilities({ stt })
    if (stt) {
      const rec = new SpeechRec()
      rec.lang = 'en-US'
      rec.interimResults = false
      rec.maxAlternatives = 1
      rec.onresult = (e: any) => {
        const transcript = e.results?.[0]?.[0]?.transcript || ''
        if (transcript) {
          // Stop recording immediately and send message right away
          setIsRecording(false)
          try {
            recognitionRef.current.stop()
          } catch (_e) {}
          // Auto send when transcript captured - no delay
          handleSendMessage(transcript)
        }
      }
      rec.onend = () => {
        setIsRecording(false)
      }
      recognitionRef.current = rec
    }
  }, [])

  const startRecording = () => {
    if (!hasVoiceCapabilities.stt || !recognitionRef.current) return
    try {
      recognitionRef.current.start()
      setIsRecording(true)
    } catch (_e) {
      setIsRecording(false)
    }
  }

  const stopRecording = () => {
    try { recognitionRef.current?.stop() } catch (_e) {}
    setIsRecording(false)
  }

  const speak = async (text: string) => {
    if (!text) return
    
    // Don't speak if muted
    if (!autoSpeak) {
      return
    }
    
    // Stop any currently playing audio before starting new speech
    elevenLabsService.stopCurrentAudio()
    
    // Replace "1031" with "ten thirty one" for proper pronunciation
    const speakableText = text.replace(/\b1031\b/gi, 'ten thirty one')
    
    // Stop mic while Neela is speaking
    if (isRecording && recognitionRef.current) {
      try {
        recognitionRef.current.stop()
        setIsRecording(false)
      } catch (error) {
      }
    }
    
    // ONLY use ElevenLabs - no browser TTS fallback
    const ELEVENLABS_API_KEY = import.meta.env.VITE_ELEVENLABS_API_KEY
    
    if (!ELEVENLABS_API_KEY || ELEVENLABS_API_KEY.trim() === '') {
      return
    }
    
    try {
      await elevenLabsService.speak(speakableText, {
        stability: 0.5,
        similarityBoost: 0.75,
        style: 0.0,
        useSpeakerBoost: true,
        voiceId: '21m00Tcm4TlvDq8ikWAM' // Rachel voice
      })
      // Restart mic immediately after ElevenLabs finishes speaking if voice mode is active
      if (autoSpeak && hasVoiceCapabilities.stt && recognitionRef.current) {
        try {
          recognitionRef.current.start()
          setIsRecording(true)
        } catch (error) {
        }
      }
    } catch (error) {
      if (error instanceof Error) {
      }
      // Do NOT fall back to browser TTS - just fail silently
      // Restart mic immediately even if ElevenLabs fails
      if (autoSpeak && hasVoiceCapabilities.stt && recognitionRef.current) {
        try {
          recognitionRef.current.start()
          setIsRecording(true)
        } catch (error) {
        }
      }
    }
  }
  

  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const [quickQuestionsCollapsed, setQuickQuestionsCollapsed] = useState(false)

  useEffect(() => {
    if (showRegistrationForm && !registrationFormMessageId) {
      setQuickQuestionsCollapsed(true)
      const id = `registration-form-${Date.now()}`
      setRegistrationFormMessageId(id)
      setMessages(prev => [...prev, {
        id,
        content: '',
        role: 'assistant',
        timestamp: new Date(),
        kind: 'registrationForm'
      }])
    }
  }, [showRegistrationForm, registrationFormMessageId])

  const finalizeRegistrationForm = (messageId: string, content: string) => {
    setMessages(prev => prev.map((m) => {
      if (m.id !== messageId) return m
      return { ...m, kind: undefined, content }
    }))
    setRegistrationFormMessageId(null)
    setShowRegistrationForm(false)
    setRegistrationPasswordVisible(false)
    setRegistrationConfirmPasswordVisible(false)
    setRegistrationData({ firstName: '', lastName: '', phone: '', email: '', password: '', confirmPassword: '', agreeToTerms: false })
  }
  // Voice preference state
  const [voicePreferenceSet, setVoicePreferenceSet] = useState(false)
  
  // Initialize messages based on authentication status
  const getInitialMessage = (): Message => {
    if (isAuthenticated && user) {
      return {
        id: '1',
        content: `Welcome back, ${user.firstName}! I'm Neela, your friendly AI assistant. I'm here to help you with your 1031 exchange - I can chat with you via voice, or we can type back and forth. What would you prefer?`,
        role: 'assistant',
        timestamp: new Date(),
        buttons: [
          { action: 'prefer-voice', label: 'Voice', icon: <Mic className="h-4 w-4" /> },
          { action: 'prefer-text', label: 'Text', icon: <MessageCircle className="h-4 w-4" /> }
        ]
      }
    }
    return {
      id: '1',
      content: "Hello! I'm Neela, your friendly AI assistant here to help with 1031 exchanges. I'm here to make this process as smooth as possible for you. Would you like to chat by voice or text? I can speak with you, or we can type back and forth - whatever feels most comfortable!",
      role: 'assistant',
      timestamp: new Date(),
      buttons: [
        { action: 'prefer-voice', label: 'Voice', icon: <Mic className="h-4 w-4" /> },
        { action: 'prefer-text', label: 'Text', icon: <MessageCircle className="h-4 w-4" /> }
      ]
    }
  }

  const [messages, setMessages] = useState<Message[]>([getInitialMessage()])
  
  // Speak initial message when component mounts (ElevenLabs only)
  useEffect(() => {
    if (autoSpeak && messages.length === 1 && !voicePreferenceSet) {
      const initialMessage = messages[0]
      if (initialMessage.role === 'assistant') {
        // Wait a bit for ElevenLabs to be ready, then speak
        setTimeout(() => {
          speak(initialMessage.content)
        }, 1000)
      }
    }
  }, [autoSpeak, messages.length, voicePreferenceSet])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  
  // Email capture for starting exchange
  const [isCollectingEmail, setIsCollectingEmail] = useState(false)
  const [capturedEmail, setCapturedEmail] = useState<string>('')
  
  // Book call flow state
  const [isBookingCall, setIsBookingCall] = useState(false)
  const [bookCallData, setBookCallData] = useState({
    name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    phone: user?.phone || '',
    date: '',
    time: '',
    contactMethod: 'email' as 'email' | 'phone',
    notes: '',
    exchangeType: 'complex' as 'reverse' | 'tic' | 'complex'
  })
  const [bookCallStep, setBookCallStep] = useState<'greeting' | 'name' | 'email' | 'phone' | 'date' | 'time' | 'contact' | 'notes' | 'type' | 'confirm'>('greeting')

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    return () => {
      elevenLabsService.stopCurrentAudio()
    }
  }, [])

  // Removed automatic signup prompt - only show when user explicitly asks

  // Update bookCallData when user changes
  useEffect(() => {
    if (user) {
      setBookCallData(prev => ({
        ...prev,
        name: prev.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || ''
      }))
    }
  }, [user])

  // Handle book call intent
  useEffect(() => {
    if (bookCallIntent && !isBookingCall) {
      setIsBookingCall(true)
      setBookCallStep('greeting')
      const greetingMessage: Message = {
        id: Date.now().toString(),
        content: `Hi${user ? ` ${user.firstName}` : ''}! I'd be happy to help you book a call with Allie, our 1031 exchange expert. ${user ? 'I have some of your information already, but let me confirm a few details.' : 'Let me collect some information to schedule your consultation.'}\n\nWhat type of exchange are you working on?`,
        role: 'assistant',
        timestamp: new Date(),
        buttons: [
          { action: 'select-exchange-reverse', label: 'Reverse Exchange', icon: <Shield className="h-4 w-4" /> },
          { action: 'select-exchange-tic', label: 'TIC Exchange', icon: <Users className="h-4 w-4" /> },
          { action: 'select-exchange-complex', label: 'Complex Exchange', icon: <CheckCircle className="h-4 w-4" /> }
        ]
      }
      setMessages(prev => [...prev, greetingMessage])
      if (autoSpeak) speak(greetingMessage.content)
      onBookCallIntentHandled?.()
    }
  }, [bookCallIntent, isBookingCall, user, autoSpeak, onBookCallIntentHandled])

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputValue
    if (!textToSend.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      content: textToSend,
      role: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)

    // Handle book call flow
    if (isBookingCall) {
      const lowerInput = textToSend.toLowerCase().trim()
      let nextStep = bookCallStep
        let response = ''
        let updatedData = { ...bookCallData }

        if (bookCallStep === 'greeting' || bookCallStep === 'type') {
          // Detect exchange type
          if (lowerInput.includes('reverse')) {
            updatedData.exchangeType = 'reverse'
            nextStep = user ? 'date' : 'name'
            const exchangeTypeLabel = 'Reverse Exchange'
            response = user 
              ? `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Schedule a consultation for your ${exchangeTypeLabel.toLowerCase()}.\n\n**Your Information:**\n• **Name:** ${bookCallData.name || user.firstName + ' ' + user.lastName}\n• **Email:** ${bookCallData.email || user.email}\n• **Phone:** ${bookCallData.phone || user.phone || 'Not provided'}\n\nWhen would you like to schedule the call?`
              : `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Let's start with your name. What's your full name?`
          } else if (lowerInput.includes('tic') || lowerInput.includes('tenant')) {
            updatedData.exchangeType = 'tic'
            nextStep = user ? 'date' : 'name'
            const exchangeTypeLabel = 'TIC Exchange'
            response = user 
              ? `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Schedule a consultation for your ${exchangeTypeLabel.toLowerCase()}.\n\n**Your Information:**\n• **Name:** ${bookCallData.name || user.firstName + ' ' + user.lastName}\n• **Email:** ${bookCallData.email || user.email}\n• **Phone:** ${bookCallData.phone || user.phone || 'Not provided'}\n\nWhen would you like to schedule the call?`
              : `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Let's start with your name. What's your full name?`
          } else if (lowerInput.includes('complex') || lowerInput.includes('standard') || lowerInput.includes('regular')) {
            updatedData.exchangeType = 'complex'
            nextStep = user ? 'date' : 'name'
            const exchangeTypeLabel = 'Complex Exchange'
            response = user 
              ? `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Schedule a consultation for your ${exchangeTypeLabel.toLowerCase()}.\n\n**Your Information:**\n• **Name:** ${bookCallData.name || user.firstName + ' ' + user.lastName}\n• **Email:** ${bookCallData.email || user.email}\n• **Phone:** ${bookCallData.phone || user.phone || 'Not provided'}\n\nWhen would you like to schedule the call?`
              : `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Let's start with your name. What's your full name?`
          } else {
            response = 'I didn\'t catch that. Could you please specify: Reverse Exchange, TIC Exchange, or Complex Exchange?'
            const errorMessage: Message = {
              id: (Date.now() + 1).toString(),
              content: response,
              role: 'assistant',
              timestamp: new Date(),
              buttons: [
                { action: 'select-exchange-reverse', label: 'Reverse Exchange', icon: <Shield className="h-4 w-4" /> },
                { action: 'select-exchange-tic', label: 'TIC Exchange', icon: <Users className="h-4 w-4" /> },
                { action: 'select-exchange-complex', label: 'Complex Exchange', icon: <CheckCircle className="h-4 w-4" /> }
              ]
            }
            setMessages(prev => [...prev, errorMessage])
            if (autoSpeak) speak(response)
            setIsLoading(false)
            return
          }
          
          // Add date input for authenticated users
          if (user && nextStep === 'date') {
            const dateMessage: Message = {
              id: (Date.now() + 1).toString(),
              content: response,
              role: 'assistant',
              timestamp: new Date(),
              bookCallData: { exchangeType: updatedData.exchangeType, showDateInput: true }
            }
            setBookCallData(updatedData)
            setBookCallStep(nextStep)
            setMessages(prev => [...prev, dateMessage])
            if (autoSpeak) speak(response)
            setIsLoading(false)
            return
          }
        } else if (bookCallStep === 'name') {
          updatedData.name = textToSend.trim()
          nextStep = 'email'
          response = `Thanks, ${updatedData.name.split(' ')[0]}! What's your email address?`
        } else if (bookCallStep === 'email') {
          const emailMatch = textToSend.match(/[\w\.-]+@[\w\.-]+\.\w+/)
          if (emailMatch) {
            updatedData.email = emailMatch[0]
            nextStep = 'phone'
            response = `Got it! What's your phone number?`
          } else {
            response = 'That doesn\'t look like a valid email address. Could you please provide your email?'
            setIsLoading(false)
            return
          }
        } else if (bookCallStep === 'phone') {
          const phoneMatch = textToSend.match(/[\d\s\-\(\)\+]+/)
          if (phoneMatch) {
            updatedData.phone = phoneMatch[0].trim()
            nextStep = 'date'
            response = 'Perfect! When would you like to schedule the call? Please provide a preferred date (e.g., "January 15" or "next Tuesday").'
          } else {
            response = 'Could you please provide your phone number?'
            setIsLoading(false)
            return
          }
        } else if (bookCallStep === 'date') {
          // Try to parse date from input
          const today = new Date()
          const tomorrow = new Date(today)
          tomorrow.setDate(tomorrow.getDate() + 1)
          
          // Simple date parsing (can be enhanced)
          let parsedDate = ''
          if (lowerInput.includes('tomorrow')) {
            parsedDate = tomorrow.toISOString().split('T')[0]
          } else if (lowerInput.includes('next week')) {
            const nextWeek = new Date(today)
            nextWeek.setDate(nextWeek.getDate() + 7)
            parsedDate = nextWeek.toISOString().split('T')[0]
          } else {
            // Try to extract date pattern
            const dateMatch = textToSend.match(/\d{1,2}[\/\-]\d{1,2}[\/\-]?\d{0,4}/)
            if (dateMatch) {
              // Simple date parsing - in production, use a proper date library
              parsedDate = dateMatch[0]
            }
          }

          if (parsedDate || lowerInput.match(/\d{1,2}/)) {
            updatedData.date = parsedDate || textToSend.trim()
            nextStep = 'time'
            const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM']
            response = 'Great! What time works best for you?'
            // Add time slots as buttons
            const timeMessage: Message = {
              id: (Date.now() + 1).toString(),
              content: response,
              role: 'assistant',
              timestamp: new Date(),
              buttons: timeSlots.map(time => ({
                action: `select-time-${time}`,
                label: time,
                icon: <Clock className="h-3 w-3" />
              })),
              bookCallData: { timeSlots }
            }
            setBookCallData(updatedData)
            setBookCallStep(nextStep)
            setMessages(prev => [...prev, timeMessage])
            if (autoSpeak) speak(response)
            setIsLoading(false)
            return
          } else {
            response = 'Could you please provide a specific date? For example, "January 15" or "next Tuesday".'
            setIsLoading(false)
            return
          }
        } else if (bookCallStep === 'time') {
          const timeMatch = textToSend.match(/(\d{1,2}):?(\d{0,2})\s?(AM|PM|am|pm)/i) || textToSend.match(/(\d{1,2})\s?(AM|PM|am|pm)/i)
          if (timeMatch) {
            updatedData.time = textToSend.trim()
            nextStep = 'contact'
            response = 'Perfect! How would you prefer Allie to contact you?'
            const contactMessage: Message = {
              id: (Date.now() + 1).toString(),
              content: response,
              role: 'assistant',
              timestamp: new Date(),
              buttons: [
                { action: 'select-contact-email', label: 'Email', icon: <Mail className="h-4 w-4" /> },
                { action: 'select-contact-phone', label: 'Phone', icon: <PhoneIcon className="h-4 w-4" /> }
              ]
            }
            setBookCallData(updatedData)
            setBookCallStep(nextStep)
            setMessages(prev => [...prev, contactMessage])
            if (autoSpeak) speak(response)
            setIsLoading(false)
            return
          } else {
            response = 'Could you please specify a time? For example, "2 PM" or "10:00 AM".'
            setIsLoading(false)
            return
          }
        } else if (bookCallStep === 'contact') {
          if (lowerInput.includes('email')) {
            updatedData.contactMethod = 'email'
            nextStep = 'notes'
            response = 'Email it is! Is there anything specific you\'d like Allie to know before the call? (This is optional)'
            const notesMessage: Message = {
              id: (Date.now() + 1).toString(),
              content: response,
              role: 'assistant',
              timestamp: new Date(),
              bookCallData: { showNotesInput: true }
            }
            setBookCallData(updatedData)
            setBookCallStep(nextStep)
            setMessages(prev => [...prev, notesMessage])
            if (autoSpeak) speak(response)
            setIsLoading(false)
            return
          } else if (lowerInput.includes('phone')) {
            updatedData.contactMethod = 'phone'
            nextStep = 'notes'
            response = 'Phone call confirmed! Is there anything specific you\'d like Allie to know before the call? (This is optional)'
            const notesMessage: Message = {
              id: (Date.now() + 1).toString(),
              content: response,
              role: 'assistant',
              timestamp: new Date(),
              bookCallData: { showNotesInput: true }
            }
            setBookCallData(updatedData)
            setBookCallStep(nextStep)
            setMessages(prev => [...prev, notesMessage])
            if (autoSpeak) speak(response)
            setIsLoading(false)
            return
          } else {
            response = 'Please choose either "email" or "phone" for how you\'d like to be contacted.'
            setIsLoading(false)
            return
          }
        } else if (bookCallStep === 'notes') {
          if (!lowerInput.includes('no') && !lowerInput.includes('skip') && !lowerInput.includes('nothing')) {
            updatedData.notes = textToSend.trim()
          }
          nextStep = 'confirm'
          const exchangeTypeLabel = updatedData.exchangeType === 'reverse' ? 'Reverse Exchange' : updatedData.exchangeType === 'tic' ? 'TIC Exchange' : 'Complex Exchange'
          response = `Perfect! Let me confirm your booking details:\n\n📋 **Call Booking Summary:**\n• **Name:** ${updatedData.name}\n• **Email:** ${updatedData.email}\n• **Phone:** ${updatedData.phone}\n• **Exchange Type:** ${exchangeTypeLabel}\n• **Preferred Date:** ${updatedData.date}\n• **Preferred Time:** ${updatedData.time}\n• **Contact Method:** ${updatedData.contactMethod === 'email' ? 'Email' : 'Phone'}${updatedData.notes ? `\n• **Notes:** ${updatedData.notes}` : ''}\n\nDoes this look correct?`
          const confirmMessage: Message = {
            id: (Date.now() + 1).toString(),
            content: response,
            role: 'assistant',
            timestamp: new Date(),
            buttons: [
              { action: 'confirm-booking', label: 'Yes, Book Call', icon: <CheckCircle className="h-4 w-4" /> },
              { action: 'cancel-booking', label: 'Cancel', icon: <X className="h-4 w-4" /> }
            ]
          }
          setBookCallData(updatedData)
          setBookCallStep(nextStep)
          setMessages(prev => [...prev, confirmMessage])
          if (autoSpeak) speak(response)
          setIsLoading(false)
          return
        } else if (bookCallStep === 'confirm') {
          if (lowerInput.includes('yes') || lowerInput.includes('confirm') || lowerInput.includes('correct')) {
            // Submit booking
            setIsLoading(true)
            setTimeout(() => {
              toast.success('Call request submitted! Allie will contact you soon.')
              const assistantMessage: Message = {
                id: (Date.now() + 1).toString(),
                content: '🎉 Excellent! Your call request has been submitted. Allie will contact you soon at your preferred time.\n\nYou\'ll receive a confirmation via ' + (bookCallData.contactMethod === 'email' ? 'email' : 'phone') + ' shortly. Is there anything else I can help you with today?',
                role: 'assistant',
                timestamp: new Date()
              }
              setMessages(prev => [...prev, assistantMessage])
              if (autoSpeak) speak(assistantMessage.content)
              setIsBookingCall(false)
              setBookCallStep('greeting')
              setBookCallData({
                name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
                email: user?.email || '',
                phone: user?.phone || '',
                date: '',
                time: '',
                contactMethod: 'email',
                notes: '',
                exchangeType: 'complex'
              })
              setIsLoading(false)
            }, 1500)
            return
          } else if (lowerInput.includes('no') || lowerInput.includes('change')) {
            response = 'No problem! What would you like to change? You can say "date", "time", "contact method", or "notes".'
            nextStep = 'confirm'
          } else {
            response = 'Please confirm by saying "yes" to book the call, or "no" to make changes.'
            setIsLoading(false)
            return
          }
        }

        setBookCallData(updatedData)
        setBookCallStep(nextStep)

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response,
          role: 'assistant',
          timestamp: new Date(),
          bookCallData: updatedData.exchangeType ? { exchangeType: updatedData.exchangeType } : undefined
        }
        setMessages(prev => [...prev, assistantMessage])
        setIsLoading(false)
        if (autoSpeak) speak(response)
        return
    }

    // Handle voice preference if not set yet
    if (!voicePreferenceSet) {
      const lowerInput = textToSend.toLowerCase()
      if (lowerInput.includes('voice') || lowerInput.includes('speak') || lowerInput.includes('talk') || lowerInput.includes('yes') && (lowerInput.includes('voice') || lowerInput.includes('speak'))) {
        setAutoSpeak(true)
        setVoicePreferenceSet(true)
        const responseMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: "Great! I'm listening now - go ahead and speak whenever you're ready.",
          role: 'assistant',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, responseMessage])
        setIsLoading(false)
        // Start microphone immediately for voice interaction
        if (hasVoiceCapabilities.stt && recognitionRef.current) {
          setTimeout(() => {
            try {
              recognitionRef.current.start()
              setIsRecording(true)
              toast.success('Listening... Speak now!')
            } catch (error) {
              console.error('Error starting microphone:', error)
            }
          }, 100)
        }
        // Speak immediately
        speak(responseMessage.content)
        return
      } else if (lowerInput.includes('text') || lowerInput.includes('type') || lowerInput.includes('write') || lowerInput.includes('no') && lowerInput.includes('voice')) {
        setAutoSpeak(false)
        setVoicePreferenceSet(true)
        const responseMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: "Great! I'll chat with you via text. Feel free to ask me anything about 1031 exchanges!",
          role: 'assistant',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, responseMessage])
        setIsLoading(false)
        if (autoSpeak) speak(responseMessage.content)
        return
      }
    }

    // Check if user wants to book a call
    const lowerInput = textToSend.toLowerCase()
    if (!isBookingCall && (
      lowerInput.includes('book') && (lowerInput.includes('call') || lowerInput.includes('appointment') || lowerInput.includes('meeting')) ||
      lowerInput.includes('schedule') && (lowerInput.includes('call') || lowerInput.includes('appointment') || lowerInput.includes('meeting')) ||
      lowerInput.includes('talk to') && (lowerInput.includes('expert') || lowerInput.includes('advisor') || lowerInput.includes('specialist')) ||
      lowerInput.includes('speak with') && (lowerInput.includes('expert') || lowerInput.includes('advisor') || lowerInput.includes('specialist')) ||
      lowerInput.includes('consultation') || lowerInput.includes('consult with')
    )) {
      setIsBookingCall(true)
      setBookCallStep('greeting')
      const greetingMessage: Message = {
        id: Date.now().toString(),
        content: `Hi${user ? ` ${user.firstName}` : ''}! I'd be happy to help you book a call with Allie, our 1031 exchange expert. ${user ? 'I have some of your information already, but let me confirm a few details.' : 'Let me collect some information to schedule your consultation.'}\n\nWhat type of exchange are you working on?`,
        role: 'assistant',
        timestamp: new Date(),
        buttons: [
          { action: 'select-exchange-reverse', label: 'Reverse Exchange', icon: <Shield className="h-4 w-4" /> },
          { action: 'select-exchange-tic', label: 'TIC Exchange', icon: <Users className="h-4 w-4" /> },
          { action: 'select-exchange-complex', label: 'Complex Exchange', icon: <CheckCircle className="h-4 w-4" /> }
        ]
      }
      setMessages(prev => [...prev, greetingMessage])
      setIsLoading(false)
      if (autoSpeak) speak(greetingMessage.content)
      return
    }

    // Handle email collection for starting exchange
    if (isCollectingEmail) {
      const emailMatch = textToSend.match(/[\w\.-]+@[\w\.-]+\.\w+/)
      if (emailMatch) {
        const email = emailMatch[0]
        setCapturedEmail(email)
        setIsCollectingEmail(false)
        
        // Store email in localStorage for marketing
        try {
          const existingEmails = JSON.parse(localStorage.getItem('captured_emails') || '[]')
          if (!existingEmails.includes(email)) {
            existingEmails.push(email)
            localStorage.setItem('captured_emails', JSON.stringify(existingEmails))
          }
        } catch (e) {
          console.error('Failed to store email:', e)
        }
        
        const appDownloadMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: `Perfect! I've got your email: ${email}\n\n📱 Download the Open1031 App\n\nGet started on your mobile device, or continue here in your browser. Would you like to start your 1031 exchange now?`,
          role: 'assistant',
          timestamp: new Date(),
          buttons: [
            { 
              action: 'download-ios', 
              label: 'Download for iOS', 
              icon: <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
            },
            { 
              action: 'download-android', 
              label: 'Download for Android', 
              icon: <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#34A853" d="M3.609 1.814L13.792 12 3.609 22.186a1.979 1.979 0 01-.289-1.046V2.86c0-.384.11-.762.289-1.046z"/><path fill="#FBBC04" d="M13.792 12l3.332-3.332 3.758 2.133a1.978 1.978 0 010 3.398l-3.758 2.133z"/><path fill="#4285F4" d="M3.32.768l10.472 5.9L17.124 10l-3.332 3.332L3.32 23.232A1.998 1.998 0 012 21.14V2.86a1.998 1.998 0 011.32-1.892z"/><path fill="#34A853" d="M13.792 12L3.32 1.768a2.015 2.015 0 012.289 0l14.273 8.033z"/></svg>
            },
            { 
              action: 'continue-browser', 
              label: 'Continue in Browser', 
              icon: <ArrowRight className="h-4 w-4" />
            }
          ]
        }
        setMessages(prev => [...prev, appDownloadMessage])
        setIsLoading(false)
        if (autoSpeak) speak(`Perfect! I've got your email. You can download our app or continue here in your browser. Would you like to start your exchange now?`)
        return
      } else {
        setTimeout(() => {
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            content: "I need a valid email address to get started. Please enter your email (e.g., yourname@example.com).",
            role: 'assistant',
            timestamp: new Date()
          }
          setMessages(prev => [...prev, errorMessage])
          if (autoSpeak) speak("I need a valid email address. Please enter your email.")
          setIsLoading(false)
        }, 1000)
        return
      }
    }

    // Check if user is trying to start the process (and not authenticated)
    if (!isAuthenticated && !capturedEmail && (
      lowerInput.includes('start') && 
      (lowerInput.includes('process') || lowerInput.includes('exchange')) ||
      lowerInput.includes('get started') ||
      lowerInput.includes('begin') ||
      lowerInput === 'start' ||
      lowerInput === 'start an exchange'
    )) {
      setIsCollectingEmail(true)
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "Great! I'll help you get started with your 1031 exchange. First, I'll need your email address so I can send you a link to download our mobile app and keep you updated on your exchange progress.\n\nWhat's your email address?",
        role: 'assistant',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, assistantMessage])
      setIsLoading(false)
      if (autoSpeak) speak("Great! I'll help you get started with your 1031 exchange. First, I'll need your email address so I can send you a link to download our app. What's your email address?")
      return
    }

    // After email is captured, if they want to start, show registration form
    if (!isAuthenticated && capturedEmail && (
      lowerInput.includes('start') || 
      lowerInput.includes('yes') || 
      lowerInput.includes('continue') ||
      lowerInput.includes('proceed')
    )) {
      setRegistrationData(prev => ({ ...prev, email: capturedEmail }))
      setShowRegistrationForm(true)
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "Perfect! To get started with your 1031 exchange, I'll need some basic information to create your account. This will only take a minute - just your name, email, and a password - and then we can begin the process. Let me collect your details below.",
        role: 'assistant',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, assistantMessage])
      setIsLoading(false)
      if (autoSpeak) speak(assistantMessage.content)
      return
    }

    // Check for hardcoded response first
    const ruleBasedResponse = generateAIResponse(textToSend)
    if (ruleBasedResponse) {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: ruleBasedResponse,
        role: 'assistant',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, assistantMessage])
      setIsLoading(false)
      if (autoSpeak) {
        speak(ruleBasedResponse)
      }
      return
    }

    // Use Claude as fallback for general queries
    try {
      const conversationHistory = messages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .slice(-10)
        .map(msg => ({
          role: msg.role,
          content: msg.content
        })) as Array<{ role: 'user' | 'assistant'; content: string }>

      conversationHistory.push({
        role: 'user',
        content: textToSend
      })

      let systemPrompt = `You are Neela, a helpful AI assistant for Open1031, a platform that helps people with 1031 exchanges. You speak in a clear, professional, and friendly manner. 

Key information about 1031 exchanges:
- A 1031 exchange allows investors to defer capital gains taxes when selling investment property
- There are strict timelines: 45 days to identify replacement properties, 180 days total to complete the exchange
- Properties must be "like-kind" (investment real estate)
- A Qualified Intermediary (QI) is required
- Open1031 serves as both QI and Registered Investment Advisor

Be helpful, accurate, and straightforward. Avoid overly enthusiastic language. Keep responses concise and informative.`

      if (isAuthenticated && user) {
        const propertySaleDate = new Date()
        propertySaleDate.setDate(propertySaleDate.getDate() - 40)
        const totalDaysFromSale = Math.floor((new Date().getTime() - propertySaleDate.getTime()) / (1000 * 60 * 60 * 24))
        const idDaysRemaining = Math.max(0, 45 - totalDaysFromSale)
        const exchangeDaysRemaining = Math.max(0, 180 - totalDaysFromSale)
        
        systemPrompt += `

Current user context:
- User name: ${user.firstName} ${user.lastName}
- Days since property sale: ${totalDaysFromSale}
- Days remaining for identification: ${idDaysRemaining}
- Days remaining for exchange completion: ${exchangeDaysRemaining}
- Identity verified: ${user.isIdentityVerified ? 'Yes' : 'No'}

When answering questions about their exchange, reference these specific timelines and their current status.`
      }

      const aiResponse = await llmApi.chat(conversationHistory, systemPrompt)
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        role: 'assistant',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, assistantMessage])
      setIsLoading(false)
      if (autoSpeak) {
        speak(aiResponse)
      }
    } catch (error) {
      console.error('Error calling Claude:', error)
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: error instanceof Error && error.message.includes('API key') 
          ? "I'm having trouble connecting to my AI service. Please check that VITE_ANTHROPIC_API_KEY is set in your .env file."
          : "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.",
        role: 'assistant',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
      setIsLoading(false)
      if (autoSpeak) {
        speak(errorMessage.content)
      }
    }
  }

  const generateAIResponse = (userInput: string): string | null => {
    const input = userInput.toLowerCase()
    
    // Support-specific responses for authenticated users with actual data
    if (isAuthenticated && user) {
      if (input.includes('timeline') || input.includes('deadline') || input.includes('days')) {
        // Calculate actual timeline data (matching DashboardPage logic)
        const propertySaleDate = new Date()
        propertySaleDate.setDate(propertySaleDate.getDate() - 40) // Using same 40-day offset
        const totalDaysFromSale = Math.floor((new Date().getTime() - propertySaleDate.getTime()) / (1000 * 60 * 60 * 24))
        const idDaysRemaining = Math.max(0, 45 - totalDaysFromSale)
        const exchangeDaysRemaining = Math.max(0, 180 - totalDaysFromSale)
        
        return `Hi ${user.firstName}! Here's your current exchange status:\n\n📅 Property Sale: Day ${totalDaysFromSale} of 180\n\n⏰ 45-Day Identification: ${idDaysRemaining} days remaining\n⏰ 180-Day Exchange: ${exchangeDaysRemaining} days remaining\n\nYou're currently ${idDaysRemaining > 0 ? 'in the identification period' : 'past the identification deadline'}. You can view your detailed timeline with countdown timers on your Dashboard. Would you like help with any specific deadline?`
      }
      
      if (input.includes('upload') || input.includes('document')) {
        return `${user.firstName}, you currently have 8 of 12 required documents uploaded. Still needed:\n\n📄 Missing Documents:\n• Property Appraisal Report\n• Title Insurance Policy\n• Identification Letter (if not submitted)\n• Closing Statement\n\nYou can upload these by going to Documents → Upload New. We accept PDFs, JPGs, and Word documents up to 10MB. Need help finding a specific document?`
      }
      
      if (input.includes('property') || input.includes('identified') || input.includes('replacement')) {
        const totalDaysFromSale = 40 // Current day in exchange
        const idDaysRemaining = Math.max(0, 45 - totalDaysFromSale)
        return `${user.firstName}, here's your property identification status:\n\n🏢 Identified Properties: 0 of 3\n⏰ Time Remaining: ${idDaysRemaining} days to identify\n\nYou can identify up to 3 replacement properties within 45 days of your sale. Browse qualified properties in the Marketplace section, or I can help you understand the identification rules. What type of property are you looking for?`
      }
      
      if (input.includes('calculate') || input.includes('savings') || input.includes('tax')) {
        return `${user.firstName}, based on your exchange:\n\n💰 Estimated Tax Savings: $125,000\n📊 Capital Gains Deferred: $500,000\n📈 Property Appreciation: 100%\n\nYour full tax calculator with detailed breakdown is available on the Calculator page. It shows capital gains, depreciation recapture, and state tax implications. Would you like me to explain any specific calculation?`
      }
      
      if (input.includes('advisor') || input.includes('contact') || input.includes('speak')) {
        return `${user.firstName}, I can connect you with your dedicated advisor:\n\n👤 Your Advisor: Sarah Johnson, CPA\n📞 Direct: (555) 123-4567\n📧 Email: sjohnson@open1031.ai\n🕐 Available: Mon-Fri 9am-6pm EST\n\nFor urgent matters, call our 24/7 support: 1-800-OPEN1031\n\nWould you like me to schedule a callback or send Sarah a message about your inquiry?`
      }
      
      if (input.includes('help') || input.includes('support') || input.includes('problem') || input.includes('issue')) {
        const totalDaysFromSale = 40
        const exchangeDaysRemaining = Math.max(0, 180 - totalDaysFromSale)
        return `Hi ${user.firstName}! I'm here to help with your 1031 exchange.\n\n📊 Your Quick Stats:\n• ${exchangeDaysRemaining} days remaining in exchange\n• 8/12 documents uploaded\n• $125K estimated tax savings\n• Identity: ${user.isIdentityVerified ? 'Verified ✓' : 'Pending'}\n\nI can assist with:\n• Timeline & deadline tracking\n• Document upload & management\n• Property identification\n• Tax calculations\n• Advisor connection\n\nWhat would you like help with?`
      }
      
      // General authenticated response
      if (!input.includes('what is') && !input.includes('how much')) {
        return `I'm here to help with your exchange, ${user.firstName}! I can provide information about your timeline, documents, properties, or connect you with your advisor. What specific question do you have?`
      }
    }
    
    // Sales responses for non-authenticated users - be conversational, don't push signup
    // Only prompt signup if user explicitly asks to create account or start
    const wantsToCreateAccount = input.includes('create account') || 
                                 input.includes('sign up') || 
                                 input.includes('register') ||
                                 input.includes('make an account') ||
                                 (input.includes('start') && (input.includes('account') || input.includes('process') || input.includes('exchange')))
    
    if (wantsToCreateAccount && !isAuthenticated) {
      setTimeout(() => {
        setShowRegistrationForm(true)
        setRegistrationData(prev => ({ ...prev, email: capturedEmail || '' }))
      }, 500)
      return "Great! I'll help you create an account. This will only take a minute - just your name, email, and a password. Once you're set up, I can guide you through your 1031 exchange step by step. Let me collect your details below."
    }
    
    if (input.includes('what is') || input.includes('1031 exchange') || input.includes('ten thirty one')) {
      return "Good question! A 1031 exchange is a tax-deferred real estate transaction that allows you to sell an investment property and purchase a 'like-kind' replacement property without paying capital gains taxes immediately. You can save thousands or even millions in taxes while reinvesting the full proceeds into a new property.\n\nFeel free to ask me anything else about 1031 exchanges - I'm here to help!"
    }
    
    if (input.includes('how much') || input.includes('save') || input.includes('tax')) {
      return "Well, the tax savings depend on your property's appreciation and your tax bracket. For example, if you sell a property for $1M that you bought for $500K, you'd typically owe capital gains tax on the $500K profit. With a 1031 exchange, you defer this tax and can reinvest the full $1M into a new property. This can save you $100K or more in taxes depending on your situation - quite significant, isn't it?\n\nIs there anything else you'd like to know about the tax benefits?"
    }
    
    if (input.includes('time') || input.includes('deadline') || input.includes('45 day') || input.includes('180 day')) {
      return "Right, there are strict timelines for 1031 exchanges: you have 45 days from the sale to identify potential replacement properties, and 180 days total to complete the purchase. These deadlines are non-negotiable, I'm afraid, so it's crucial to work with experienced professionals who can help you meet them.\n\nOur platform helps you track these deadlines automatically, which makes it much easier. Do you have any other questions about the timeline?"
    }
    
    if (input.includes('qualified') || input.includes('intermediary') || input.includes('qi')) {
      return "A Qualified Intermediary, or QI, is required for all 1031 exchanges. They hold your sale proceeds in escrow and facilitate the exchange. Open1031 serves as both your QI and Registered Investment Advisor, providing a complete solution with expert guidance throughout the process.\n\nWhat else would you like to know about the exchange process?"
    }
    
    if (input.includes('property') || input.includes('like-kind') || input.includes('replacement')) {
      return "Replacement properties must be 'like-kind' to your relinquished property - meaning they must be held for investment or business purposes. This includes rental properties, commercial real estate, land, and even Delaware Statutory Trusts, or DSTs. The key is that both properties must be investment-grade real estate.\n\nWe have access to thousands of qualified replacement properties, which gives you plenty of options. Is there anything specific about properties you'd like to understand better?"
    }
    
    // Only trigger signup if user explicitly wants to start/create account
    if ((input.includes('start') && (input.includes('account') || input.includes('process') || input.includes('exchange'))) || 
        input.includes('create account') || 
        input.includes('sign up') ||
        input.includes('register')) {
      if (!isAuthenticated) {
        setTimeout(() => {
          setShowRegistrationForm(true)
          setRegistrationData(prev => ({ ...prev, email: capturedEmail || '' }))
        }, 500)
        return "Perfect! To get started with your 1031 exchange, I'll need some basic information to create your account. This will only take a minute - just your name, email, and a password - and then we can begin the process. Let me collect your details below."
      }
    }
    
    if (input.includes('cost') || input.includes('fee') || input.includes('price')) {
      return "Open1031's fees are transparent and competitive. We charge a flat fee structure rather than percentage-based fees, which can save you thousands compared to traditional QI services. Our platform also includes document management, tax calculations, and access to institutional-grade replacement properties at no extra cost."
    }
    
    if (input.includes('help') || input.includes('support')) {
      return "I'm here to help! You can ask me about 1031 exchange rules, tax implications, timelines, property requirements, or any other questions. If you're ready to start the process, I can guide you through identity verification and document upload. What specific aspect would you like to explore?"
    }
    
    // Default response
    return null
  }

  // Quick questions based on authentication status
  const quickQuestions = isAuthenticated ? [
    "Check my exchange timeline",
    "Upload documents",
    "View my identified properties",
    "Calculate remaining tax savings",
    "Contact my advisor",
    "Need help with something"
  ] : [
    "What is a 1031 exchange?",
    "How much can I save in taxes?",
    "What are the time limits?",
    "What properties qualify?",
    "How do I get started?"
  ]

  // Filter suggestions based on input
  const filteredSuggestions = quickQuestions.filter(q => 
    q.toLowerCase().includes(inputValue.toLowerCase()) && inputValue.length > 0
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setShowSuggestions(value.length > 0)
  }

  const handleSuggestionClick = (suggestion: string) => {
    setShowSuggestions(false)
    // Automatically send the suggestion
    handleSendMessage(suggestion)
  }

  const handleQuickQuestion = (question: string) => {
    // Automatically send the question
    handleSendMessage(question)
  }

  return (
    <div className={cn("flex flex-col h-full bg-white dark:bg-slate-900", className)}>
      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex",
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={cn(
                "flex items-start space-x-2 max-w-[80%]",
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              )}
            >
              <div
                className={cn(
                  "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ring-1",
                  message.role === 'user' 
                    ? 'bg-gradient-to-r from-[#69adba] to-[#5a9aa6] text-white ring-[#69adba]/20 dark:ring-[#69adba]/30' 
                    : 'bg-[#69adba]/10 dark:bg-[#69adba]/20 text-[#69adba] ring-[#69adba]/20 dark:ring-[#69adba]/30'
                )}
              >
                {message.role === 'user' ? (
                  <User className="h-4 w-4" />
                ) : (
                  <Bot className="h-4 w-4" />
                )}
              </div>
              <div className={cn(
                "rounded-2xl px-4 py-2 shadow-sm",
                message.role === 'user'
                  ? 'bg-gradient-to-r from-[#69adba] to-[#5a9aa6] rounded-tr-none'
                  : 'bg-white dark:bg-slate-800 rounded-tl-none border border-slate-200 dark:border-slate-700'
              )}>
                {message.kind === 'registrationForm' ? (
                  <div className="py-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <UserPlus className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold text-sm">Create Your Account</h3>
                    </div>
                    <div className="mb-4 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSSOProvider('apple')
                          setShowSSOModal(true)
                          setTimeout(() => {
                            setShowSSOModal(false)
                            const msg: Message = {
                              id: Date.now().toString(),
                              content: 'Apple Sign-In integration is coming soon! For now, please complete the form below or use the demo account.',
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, msg])
                          }, 2000)
                        }}
                        className="inline-flex justify-center items-center py-2 px-3 border border-secondary-300 rounded-lg shadow-sm bg-black hover:bg-secondary-900 text-white text-xs font-medium transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                        Apple
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSSOProvider('google')
                          setShowSSOModal(true)
                          setTimeout(() => {
                            setShowSSOModal(false)
                            const msg: Message = {
                              id: Date.now().toString(),
                              content: 'Google Sign-In integration is coming soon! For now, please complete the form below or use the demo account.',
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, msg])
                          }, 2000)
                        }}
                        className="inline-flex justify-center items-center py-2 px-3 border border-secondary-300 dark:border-slate-600 rounded-lg shadow-sm bg-white dark:bg-slate-800 hover:bg-secondary-50 dark:hover:bg-slate-700 text-secondary-700 dark:text-slate-200 text-xs font-medium transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google
                      </button>
                    </div>
                    <div className="relative mb-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-secondary-300"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-white dark:bg-slate-800 text-secondary-500">Or fill out manually</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Input placeholder="First Name" value={registrationData.firstName} onChange={(e) => setRegistrationData(prev => ({ ...prev, firstName: e.target.value }))} />
                      <Input placeholder="Last Name" value={registrationData.lastName} onChange={(e) => setRegistrationData(prev => ({ ...prev, lastName: e.target.value }))} />
                      <Input placeholder="Phone Number" type="tel" value={registrationData.phone} onChange={(e) => setRegistrationData(prev => ({ ...prev, phone: e.target.value }))} />
                      <Input placeholder="Email Address" type="email" value={registrationData.email} onChange={(e) => setRegistrationData(prev => ({ ...prev, email: e.target.value }))} />
                      <div className="relative">
                        <Input
                          placeholder="Password"
                          type={registrationPasswordVisible ? 'text' : 'password'}
                          value={registrationData.password}
                          onChange={(e) => setRegistrationData(prev => ({ ...prev, password: e.target.value }))}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setRegistrationPasswordVisible(v => !v)}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                          aria-label={registrationPasswordVisible ? 'Hide password' : 'Show password'}
                        >
                          {registrationPasswordVisible ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          placeholder="Confirm Password"
                          type={registrationConfirmPasswordVisible ? 'text' : 'password'}
                          value={registrationData.confirmPassword}
                          onChange={(e) => setRegistrationData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setRegistrationConfirmPasswordVisible(v => !v)}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                          aria-label={registrationConfirmPasswordVisible ? 'Hide confirm password' : 'Show confirm password'}
                        >
                          {registrationConfirmPasswordVisible ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-primary/20">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={registrationData.agreeToTerms}
                          onChange={(e) => setRegistrationData(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 text-[#69adba] focus:ring-[#69adba] border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        />
                        <span className="text-xs text-secondary-600 dark:text-slate-300 leading-tight">
                          I agree to the{' '}
                          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline font-medium">
                            Terms of Service
                          </a>
                          {' '}and{' '}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline font-medium">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                    </div>
                    <div className="flex space-x-2 mt-4">
                      <Button
                        onClick={() => {
                          finalizeRegistrationForm(message.id, 'Account creation cancelled.')
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            setIsLoading(true)
                            const firstName = registrationData.firstName
                            const registerData = {
                              firstName: registrationData.firstName,
                              lastName: registrationData.lastName,
                              email: registrationData.email,
                              password: registrationData.password,
                              phone: registrationData.phone,
                              dateOfBirth: '',
                              ssn: '',
                              address: {
                                street: '',
                                city: '',
                                state: '',
                                zipCode: ''
                              }
                            }
                            const { dataPersistence } = await import('../../services/dataPersistence')
                            dataPersistence.merge({
                              firstName: registrationData.firstName,
                              lastName: registrationData.lastName,
                              email: registrationData.email,
                              phone: registrationData.phone
                            })
                            await registerUser(registerData)
                            setTimeout(() => {
                              navigate('/app')
                              toast.success(`Welcome, ${firstName}! You're now logged in.`)
                            }, 1500)
                            const newMessage: Message = {
                              id: Date.now().toString(),
                              content: `Perfect! I've created your account, ${firstName}!\n\nI'm logging you into the app now. Once you're in, we can get started with your 1031 exchange. I'll guide you through everything step by step.\n\nYou can complete identity verification later in your account settings if needed.`,
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, newMessage])
                            finalizeRegistrationForm(message.id, `Account created for ${firstName} ${registrationData.lastName}.`)
                            if (autoSpeak) speak(newMessage.content)
                          } catch (error: any) {
                            toast.error(error.message || 'Failed to create account')
                          } finally {
                            setIsLoading(false)
                          }
                        }}
                        disabled={!registrationData.firstName || !registrationData.lastName || !registrationData.phone || !registrationData.email || !registrationData.password || registrationData.password !== registrationData.confirmPassword || !registrationData.agreeToTerms}
                        size="sm"
                        className="flex-1"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Create Account & Continue
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className={cn(
                    "text-xs leading-relaxed whitespace-pre-wrap",
                    message.role === 'user' 
                      ? 'text-white' 
                      : 'text-slate-900 dark:text-slate-100'
                  )}>
                    {message.content}
                  </p>
                )}
                
                {/* Book Call Info Box */}
                {message.role === 'assistant' && message.bookCallData?.exchangeType && (
                  <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-blue-900 dark:text-blue-200 font-medium mb-1">
                          Why a consultation?
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          {message.bookCallData.exchangeType === 'reverse' && 'Reverse exchanges require specialized handling and legal considerations.'}
                          {message.bookCallData.exchangeType === 'tic' && 'TIC (Tenant-In-Common) exchanges involve complex partnership structures.'}
                          {message.bookCallData.exchangeType === 'complex' && 'Complex exchanges require expert guidance to ensure compliance and optimal outcomes.'}
                          {' '}Our team will connect you with our attorney partners to ensure everything is handled correctly.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Date Input */}
                {message.role === 'assistant' && message.bookCallData?.showDateInput && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Preferred Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="date"
                        value={bookCallData.date}
                        onChange={(e) => {
                          setBookCallData(prev => ({ ...prev, date: e.target.value }))
                          if (e.target.value) {
                            setTimeout(() => {
                              setBookCallStep('time')
                              const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM']
                              const timeMessage: Message = {
                                id: Date.now().toString(),
                                content: 'Great! What time works best for you?',
                                role: 'assistant',
                                timestamp: new Date(),
                                buttons: timeSlots.map(time => ({
                                  action: `select-time-${time}`,
                                  label: time,
                                  icon: <Clock className="h-3 w-3" />
                                })),
                                bookCallData: { timeSlots }
                              }
                              setMessages(prev => [...prev, timeMessage])
                              if (autoSpeak) speak(timeMessage.content)
                            }, 500)
                          }
                        }}
                        className="pl-10 text-xs h-8"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                )}

                {/* Time Slot Buttons */}
                {message.role === 'assistant' && message.bookCallData?.timeSlots && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Preferred Time</label>
                    <div className="grid grid-cols-3 gap-2">
                      {message.bookCallData.timeSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => {
                            setBookCallData(prev => ({ ...prev, time }))
                            setBookCallStep('contact')
                            const contactMessage: Message = {
                              id: Date.now().toString(),
                              content: 'Perfect! How would you prefer Allie to contact you?',
                              role: 'assistant',
                              timestamp: new Date(),
                              buttons: [
                                { action: 'select-contact-email', label: 'Email', icon: <Mail className="h-4 w-4" /> },
                                { action: 'select-contact-phone', label: 'Phone', icon: <PhoneIcon className="h-4 w-4" /> }
                              ]
                            }
                            setMessages(prev => [...prev, contactMessage])
                            if (autoSpeak) speak(contactMessage.content)
                          }}
                          className={cn(
                            "px-2 py-1.5 rounded-lg text-xs font-medium transition-colors",
                            bookCallData.time === time
                              ? 'bg-[#69adba] text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                          )}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                    {!bookCallData.time && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Please select a time
                      </p>
                    )}
                  </div>
                )}

                {/* Notes Input */}
                {message.role === 'assistant' && message.bookCallData?.showNotesInput && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Additional Notes (Optional)</label>
                    <textarea
                      value={bookCallData.notes}
                      onChange={(e) => setBookCallData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#69adba] focus:border-transparent text-xs"
                      placeholder="Tell us more about your exchange needs..."
                    />
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setBookCallData(prev => ({ ...prev, notes: '' }))
                          setBookCallStep('confirm')
                          const exchangeTypeLabel = bookCallData.exchangeType === 'reverse' ? 'Reverse Exchange' : bookCallData.exchangeType === 'tic' ? 'TIC Exchange' : 'Complex Exchange'
                          const confirmMessage: Message = {
                            id: Date.now().toString(),
                            content: `Perfect! Let me confirm your booking details:\n\n📋 **Call Booking Summary:**\n• **Name:** ${bookCallData.name}\n• **Email:** ${bookCallData.email}\n• **Phone:** ${bookCallData.phone}\n• **Exchange Type:** ${exchangeTypeLabel}\n• **Preferred Date:** ${bookCallData.date}\n• **Preferred Time:** ${bookCallData.time}\n• **Contact Method:** ${bookCallData.contactMethod === 'email' ? 'Email' : 'Phone'}${bookCallData.notes ? `\n• **Notes:** ${bookCallData.notes}` : ''}\n\nDoes this look correct?`,
                            role: 'assistant',
                            timestamp: new Date(),
                            buttons: [
                              { action: 'confirm-booking', label: 'Yes, Book Call', icon: <CheckCircle className="h-4 w-4" /> },
                              { action: 'cancel-booking', label: 'Cancel', icon: <X className="h-4 w-4" /> }
                            ]
                          }
                          setMessages(prev => [...prev, confirmMessage])
                          if (autoSpeak) speak(confirmMessage.content)
                        }}
                        className="text-xs"
                      >
                        Skip Notes
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setBookCallStep('confirm')
                          const exchangeTypeLabel = bookCallData.exchangeType === 'reverse' ? 'Reverse Exchange' : bookCallData.exchangeType === 'tic' ? 'TIC Exchange' : 'Complex Exchange'
                          const confirmMessage: Message = {
                            id: Date.now().toString(),
                            content: `Perfect! Let me confirm your booking details:\n\n📋 **Call Booking Summary:**\n• **Name:** ${bookCallData.name}\n• **Email:** ${bookCallData.email}\n• **Phone:** ${bookCallData.phone}\n• **Exchange Type:** ${exchangeTypeLabel}\n• **Preferred Date:** ${bookCallData.date}\n• **Preferred Time:** ${bookCallData.time}\n• **Contact Method:** ${bookCallData.contactMethod === 'email' ? 'Email' : 'Phone'}${bookCallData.notes ? `\n• **Notes:** ${bookCallData.notes}` : ''}\n\nDoes this look correct?`,
                            role: 'assistant',
                            timestamp: new Date(),
                            buttons: [
                              { action: 'confirm-booking', label: 'Yes, Book Call', icon: <CheckCircle className="h-4 w-4" /> },
                              { action: 'cancel-booking', label: 'Cancel', icon: <X className="h-4 w-4" /> }
                            ]
                          }
                          setMessages(prev => [...prev, confirmMessage])
                          if (autoSpeak) speak(confirmMessage.content)
                        }}
                        className="text-xs bg-[#69adba] hover:bg-[#5a9aa6] text-white"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                )}

                {/* Message Buttons */}
                {message.buttons && message.buttons.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {message.buttons.map((button, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (button.action === 'prefer-voice') {
                            setAutoSpeak(true)
                            setVoicePreferenceSet(true)
                            const responseMessage: Message = {
                              id: Date.now().toString(),
                              content: "Great! I'm listening now - go ahead and speak whenever you're ready.",
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, responseMessage])
                            speak(responseMessage.content)
                            // Start microphone automatically for voice interaction
                            setTimeout(() => {
                              if (hasVoiceCapabilities.stt && recognitionRef.current) {
                                try {
                                  recognitionRef.current.start()
                                  setIsRecording(true)
                                  toast.success('Listening... Speak now!')
                                } catch (error) {
                                  console.error('Error starting microphone:', error)
                                }
                              }
                            }, 2000)
                          } else if (button.action === 'prefer-text') {
                            setAutoSpeak(false)
                            setVoicePreferenceSet(true)
                            const responseMessage: Message = {
                              id: Date.now().toString(),
                              content: "Perfect! I'll chat with you via text then. Feel free to ask me anything about 1031 exchanges, or if you're ready to get started, just type 'start' or 'start an exchange' and I'll guide you through everything!",
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, responseMessage])
                          } else if (button.action === 'start-signup') {
                            setShowRegistrationForm(true)
                            const signupMessage: Message = {
                              id: Date.now().toString(),
                              content: "Perfect! To get started with your 1031 exchange, I'll need some basic information to create your account. This will only take a minute, and then we can begin the process. Let me collect your details below.",
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, signupMessage])
                            if (autoSpeak) speak(signupMessage.content)
                          } else if (button.action === 'maybe-later') {
                            const laterMessage: Message = {
                              id: Date.now().toString(),
                              content: "No problem at all! Feel free to ask me any questions about 1031 exchanges - I'm here to help. When you're ready to get started, just say 'start' or 'create account' and I'll help you set everything up. Take your time!",
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, laterMessage])
                            if (autoSpeak) speak(laterMessage.content)
                          } else if (button.action === 'start-exchange') {
                            onStartProcess?.()
                            const exchangeMessage: Message = {
                              id: Date.now().toString(),
                              content: "Great! Let's start your 1031 exchange. I'll guide you through the Sales Hub where you can enter information about the property you're selling. This information will appear on your dashboard once you've entered it.",
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, exchangeMessage])
                            if (autoSpeak) speak(exchangeMessage.content)
                          } else if (button.action.startsWith('select-exchange-')) {
                            const type = button.action.replace('select-exchange-', '') as 'reverse' | 'tic' | 'complex'
                            setBookCallData(prev => ({ ...prev, exchangeType: type }))
                            setBookCallStep(user ? 'date' : 'name')
            const exchangeTypeLabel = type === 'reverse' ? 'Reverse Exchange' : type === 'tic' ? 'TIC Exchange' : 'Complex Exchange'
                            const messageContent = user 
                              ? `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Schedule a consultation for your ${exchangeTypeLabel.toLowerCase()}.\n\n**Your Information:**\n• **Name:** ${bookCallData.name || user.firstName + ' ' + user.lastName}\n• **Email:** ${bookCallData.email || user.email}\n• **Phone:** ${bookCallData.phone || user.phone || 'Not provided'}\n\nWhen would you like to schedule the call?`
                              : `Perfect! I've noted you're working on a ${exchangeTypeLabel}. Let's start with your name. What's your full name?`
            const nextMessage: Message = {
              id: Date.now().toString(),
                              content: messageContent,
              role: 'assistant',
              timestamp: new Date(),
              bookCallData: { exchangeType: type, showDateInput: user ? true : false }
            }
            setMessages(prev => [...prev, nextMessage])
            if (autoSpeak) speak(nextMessage.content)
                          } else if (button.action.startsWith('select-time-')) {
                            const time = button.action.replace('select-time-', '')
                            setBookCallData(prev => ({ ...prev, time }))
                            setBookCallStep('contact')
                            const contactMessage: Message = {
                              id: Date.now().toString(),
                              content: 'Perfect! How would you prefer Allie to contact you?',
                              role: 'assistant',
                              timestamp: new Date(),
                              buttons: [
                                { action: 'select-contact-email', label: 'Email', icon: <Mail className="h-4 w-4" /> },
                                { action: 'select-contact-phone', label: 'Phone', icon: <PhoneIcon className="h-4 w-4" /> }
                              ]
                            }
                            setMessages(prev => [...prev, contactMessage])
                            if (autoSpeak) speak(contactMessage.content)
                          } else if (button.action === 'select-contact-email') {
                            setBookCallData(prev => ({ ...prev, contactMethod: 'email' }))
                            setBookCallStep('notes')
                            const notesMessage: Message = {
                              id: Date.now().toString(),
                              content: 'Email it is! Is there anything specific you\'d like Allie to know before the call? (This is optional)',
                              role: 'assistant',
                              timestamp: new Date(),
                              bookCallData: { showNotesInput: true }
                            }
                            setMessages(prev => [...prev, notesMessage])
                            if (autoSpeak) speak(notesMessage.content)
                          } else if (button.action === 'select-contact-phone') {
                            setBookCallData(prev => ({ ...prev, contactMethod: 'phone' }))
                            setBookCallStep('notes')
                            const notesMessage: Message = {
                              id: Date.now().toString(),
                              content: 'Phone call confirmed! Is there anything specific you\'d like Allie to know before the call? (This is optional)',
                              role: 'assistant',
                              timestamp: new Date(),
                              bookCallData: { showNotesInput: true }
                            }
                            setMessages(prev => [...prev, notesMessage])
                            if (autoSpeak) speak(notesMessage.content)
                          } else if (button.action === 'confirm-booking') {
                            setIsLoading(true)
                            setTimeout(() => {
                              toast.success('Call request submitted! Allie will contact you soon.')
                              const successMessage: Message = {
                                id: Date.now().toString(),
                                content: '🎉 Excellent! Your call request has been submitted. Allie will contact you soon at your preferred time.\n\nYou\'ll receive a confirmation via ' + (bookCallData.contactMethod === 'email' ? 'email' : 'phone') + ' shortly. Is there anything else I can help you with today?',
                                role: 'assistant',
                                timestamp: new Date()
                              }
                              setMessages(prev => [...prev, successMessage])
                              if (autoSpeak) speak(successMessage.content)
                              setIsBookingCall(false)
                              setBookCallStep('greeting')
                              setBookCallData({
                                name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
                                email: user?.email || '',
                                phone: user?.phone || '',
                                date: '',
                                time: '',
                                contactMethod: 'email',
                                notes: '',
                                exchangeType: 'complex'
                              })
                              setIsLoading(false)
                            }, 1500)
                          } else if (button.action === 'cancel-booking') {
                            setIsBookingCall(false)
                            setBookCallStep('greeting')
                            setBookCallData({
                              name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
                              email: user?.email || '',
                              phone: user?.phone || '',
                              date: '',
                              time: '',
                              contactMethod: 'email',
                              notes: '',
                              exchangeType: 'complex'
                            })
                            const cancelMessage: Message = {
                              id: Date.now().toString(),
                              content: 'No problem! I\'ve cancelled the booking. Is there anything else I can help you with?',
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, cancelMessage])
                            if (autoSpeak) speak(cancelMessage.content)
                          } else if (button.action === 'download-ios') {
                            window.open('https://apps.apple.com/app/open1031', '_blank')
                            toast.success('Opening App Store...')
                          } else if (button.action === 'download-android') {
                            window.open('https://play.google.com/store/apps/details?id=com.open1031.app', '_blank')
                            toast.success('Opening Google Play...')
                          } else if (button.action === 'continue-browser') {
                            if (capturedEmail) {
                              setRegistrationData(prev => ({ ...prev, email: capturedEmail }))
                            }
                            setShowRegistrationForm(true)
                            const assistantMessage: Message = {
                              id: (Date.now() + 1).toString(),
                              content: "Perfect! To get started with your 1031 exchange, I'll need some basic information to create your account. This will only take a minute, and then we can begin the process. Let me collect your details below.",
                              role: 'assistant',
                              timestamp: new Date()
                            }
                            setMessages(prev => [...prev, assistantMessage])
                            if (autoSpeak) speak(assistantMessage.content)
                          }
                        }}
                        className={cn(
                          "text-xs px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1.5",
                          button.action.includes('select-contact-') || button.action.includes('select-exchange-')
                            ? "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 border-2 border-slate-200 dark:border-slate-700 hover:border-[#69adba]"
                            : button.action === 'confirm-booking'
                            ? "bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
                            : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                        )}
                      >
                        {button.icon}
                        {button.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-start space-x-2 max-w-[80%]">
              <div className="w-8 h-8 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0 ring-1 ring-[#69adba]/20 dark:ring-[#69adba]/30">
                <Bot className="h-4 w-4 text-[#69adba]" />
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-none px-4 py-3 border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex space-x-1.5">
                  <div className="w-2 h-2 bg-[#69adba] dark:bg-[#69adba] rounded-full animate-bounce opacity-60" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-[#69adba] dark:bg-[#69adba] rounded-full animate-bounce opacity-60" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-[#69adba] dark:bg-[#69adba] rounded-full animate-bounce opacity-60" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions */}
      {messages.length === 1 && (
        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <button
            onClick={() => setQuickQuestionsCollapsed(!quickQuestionsCollapsed)}
            className="w-full p-3 flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Quick questions:</p>
            {quickQuestionsCollapsed ? (
              <ChevronDown className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            ) : (
              <ChevronUp className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            )}
          </button>
          {!quickQuestionsCollapsed && (
            <div className="px-4 pb-4 flex flex-wrap gap-2">
              {quickQuestions.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickQuestion(question)}
                  className="text-xs h-7 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-[#69adba]/10 dark:hover:bg-[#69adba]/20 hover:border-[#69adba] hover:text-[#69adba]"
                >
                  {question}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Voice-First Input */}
      <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 relative">
        {/* Input Container */}
        <div className="px-3 pb-3">
          {/* Suggestions Dropdown */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="absolute bottom-full left-3 right-3 mb-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
              {filteredSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm border-b border-slate-200 dark:border-slate-700 last:border-b-0 text-slate-900 dark:text-slate-100"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Modern Input Layout */}
          <div className="flex items-center space-x-2 bg-slate-100 dark:bg-slate-800 rounded-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 shadow-sm relative">
            <MessageCircle className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => setShowSuggestions(inputValue.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Type a message..."
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              disabled={isLoading}
              className="flex-1 text-xs h-auto bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
            />
            
            {/* Voice Icon Inside Input */}
            {hasVoiceCapabilities.stt && (
              <button
                type="button"
                onClick={() => (isRecording ? stopRecording() : startRecording())}
                disabled={isLoading}
                className={cn(
                  "relative flex-shrink-0 p-1.5 rounded-full transition-all duration-200",
                  isRecording 
                    ? "bg-red-500 text-white animate-pulse" 
                    : "text-slate-500 dark:text-slate-400 hover:text-[#69adba] dark:hover:text-[#69adba] hover:bg-slate-200 dark:hover:bg-slate-700",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
                title={isRecording ? "Stop recording" : "Tap to speak"}
              >
                {isRecording ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </button>
            )}

            {/* Mute Neela Toggle */}
            <button
                type="button"
                onClick={() => {
                  const newValue = !autoSpeak
                  setAutoSpeak(newValue)
                  // If muting, ElevenLabs will handle stopping speech automatically
                }}
                disabled={isLoading}
                className={cn(
                  "relative flex-shrink-0 p-1.5 rounded-full transition-all duration-200",
                  autoSpeak
                    ? "text-[#69adba] dark:text-[#69adba] hover:bg-slate-200 dark:hover:bg-slate-700"
                    : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
                title={autoSpeak ? "Mute Neela" : "Unmute Neela"}
              >
                {autoSpeak ? (
                  <Volume2 className="h-4 w-4" />
                ) : (
                  <VolumeX className="h-4 w-4" />
                )}
              </button>
            
            <Button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className="h-7 w-7 bg-[#69adba] hover:bg-[#5a9aa6] text-white flex-shrink-0"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
          
          {/* Listening Indicator */}
          {isRecording && (
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-red-600 dark:text-red-400 font-medium">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-red-600 dark:bg-red-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-red-600 dark:bg-red-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-red-600 dark:bg-red-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span>Listening...</span>
            </div>
          )}
        </div>
      </div>

      {/* SSO Modal - Mockup */}
      {showSSOModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="text-center">
              {/* Provider Logo */}
              <div className="mb-6 flex justify-center">
                {ssoProvider === 'apple' ? (
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-white border-2 border-secondary-200 rounded-2xl flex items-center justify-center">
                    <svg className="w-10 h-10" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                )}
              </div>

              {/* Message */}
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                {ssoProvider === 'apple' ? 'Connecting to Apple' : 'Connecting to Google'}
              </h3>
              <p className="text-sm text-secondary-600 mb-6">

                Securely authenticating...
              </p>

              {/* Loading Spinner */}
              <div className="flex justify-center">
                <LoadingSpinner size="md" />
              </div>

              {/* Info Text */}
              <p className="mt-4 text-xs text-secondary-500">
                Demo flow - Integration coming soon!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
